import { ChevronDown, ChevronUp } from 'lucide-react';
import type { HTMLAttributes, ReactNode, TableHTMLAttributes, ThHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';
import type { SortDir } from '@/lib/sort';

export function Table({ className, ...rest }: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('tbl', className)} {...rest} />
    </div>
  );
}

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  right?: boolean | undefined;
  /** When set, the header becomes a sort control. */
  sortKey?: string | undefined;
  sort?: { column: string | null; dir: SortDir; toggle: (column: string) => void } | undefined;
}

export function Th({ right, sortKey, sort, className, children, ...rest }: ThProps) {
  const active = sortKey !== undefined && sort?.column === sortKey;
  const sortable = sortKey !== undefined && sort !== undefined;
  return (
    <th
      className={cn(right && 'r', sortable && 'sortable', className)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      onClick={sortable ? () => sort.toggle(sortKey) : undefined}
      {...rest}
    >
      <span className={cn('inline-flex items-center gap-1', right && 'flex-row-reverse')}>
        {children}
        {active ? (
          sort.dir === 'asc' ? (
            <ChevronUp className="size-3" />
          ) : (
            <ChevronDown className="size-3" />
          )
        ) : null}
      </span>
    </th>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-7 text-center text-ink3">
        {children}
      </td>
    </tr>
  );
}

export function Card({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-line2 bg-surface shadow-card',
        className,
      )}
      {...rest}
    />
  );
}

export function CardHeader({
  className,
  title,
  right,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & { title?: ReactNode; right?: ReactNode }) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2.5 border-b border-line2 px-4 py-3',
        className,
      )}
      {...rest}
    >
      {title ? <h2 className="text-sm font-bold">{title}</h2> : null}
      {children}
      {right ? <div className="ml-auto flex items-center gap-2">{right}</div> : null}
    </div>
  );
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-4', className)} {...rest} />;
}
